import { useRef, useEffect, useState, useMemo } from "react"
import { fabric } from "fabric"
import { CaretRightOutlined } from "@ant-design/icons"
import styles from "./index.module.scss"
import PicViewerBottom from "../PicViewerBottom"
import Utils from '@utils/utils'

const MIN_ZOOM = 0.1
const MAX_ZOOM = 20
const ZOOM_STEP = 0.999

// 常见视频扩展名，用于判断 url 是否为视频资源
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i

/** 根据 url 扩展名判断是否为视频 */
function isVideoUrl(url) {
    if (!url) return false
    return VIDEO_EXTENSIONS.test(url)
}

/**
 * PicViewerCenter - 中间画布区域
 * 支持两种模式：
 *   mode=0 单画布 —— 仅展示当前结果图
 *   mode=1 双画布 —— 左侧展示 extraImages（可切换），右侧展示结果图
 */
export default function PicViewerCenter({
    image = {},
    mode = 0, // 0: 单画布  1: 双画布
    onPrev,
    onNext,
    hasPrev,
    hasNext,
    onStatusChange,
}) {
    // 双画布模式下，左侧画布当前选中的 extraImage 索引
    const [activeExtraIndex, setActiveExtraIndex] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // 主图切换时重置 extraImage 选中项
    useEffect(() => {
        setActiveExtraIndex(0)
    }, [image.id, image.url])

    // 键盘导航：左右/上下键切换上一张/下一张
    useEffect(() => {
        const handleKeyDown = (e) => {
            // 弹窗打开时跳过导航
            if (isModalOpen) return
            switch (e.key) {
                case 'ArrowLeft':
                case 'ArrowUp':
                    e.preventDefault()
                    if (hasPrev) onPrev()
                    break
                case 'ArrowRight':
                case 'ArrowDown':
                    e.preventDefault()
                    if (hasNext) onNext()
                    break
                default:
                    break
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [hasPrev, hasNext, onPrev, onNext, isModalOpen])

    const extraImages = image.extraImages || []
    // 当前左侧画布展示的 extraImage
    const activeExtraImage = extraImages[activeExtraIndex]

    // 根据 mode 确定画布模式，用于条件渲染
    const canvasMode = useMemo(() => {
        if (mode == 1 && extraImages.length > 0) return "DualCanvas"
        return "SingleCanvas"
    }, [mode, extraImages])

    return (
        <div className={styles.PicViewerCenter}>
            {/* 画布区域：根据 mode 切换单/双画布布局 */}
            <div className={`${styles.CanvasArea} ${canvasMode == "DualCanvas" ? styles.DualCanvasArea : styles.SingleCanvasArea}`}>
                {/* 双画布模式 - 左侧画布列 */}
                {canvasMode == "DualCanvas" && (
                    <div className={styles.LeftCanvasColumn}>
                        {/* 左上角标签显示当前 extraImage 名称 */}
                        {activeExtraImage && (
                            <div className={styles.CanvasTag}>{activeExtraImage.label}</div>
                        )}
                        <div className={styles.CanvasWrapper}>
                            {/* 根据 url 类型选择渲染图片画布或视频播放器 */}
                            <MediaViewer
                                url={activeExtraImage?.url}
                                className={styles.ImageArea}
                            />
                        </div>
                        {/* 底部缩略图切换条 */}
                        {extraImages.length > 0 && (
                            <div className={styles.ExtraImageBar}>
                                {extraImages.map((item, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.ExtraThumb} ${activeExtraIndex === index ? styles.ExtraThumbActive : ''}`}
                                        onClick={() => setActiveExtraIndex(index)}
                                        title={item.label}
                                    >
                                        {/* 视频缩略图显示播放图标覆盖层 */}
                                        {isVideoUrl(item.url) ? (
                                            <div className={styles.VideoThumbOverlay}>
                                                <CaretRightOutlined />
                                            </div>
                                        ) : (
                                            <img src={Utils.getImgThumbnail(item.url)} alt={item.label} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {/* 单画布模式 / 双画布模式的右侧结果图画布 */}
                <div className={styles.RightCanvasColumn}>
                    {image?.name && <div className={styles.CanvasTag}>{image?.name}</div>}
                    <div className={styles.CanvasWrapper}>
                        {/* 根据 url 类型选择渲染图片画布或视频播放器 */}
                        <MediaViewer
                            url={image.url}
                            className={styles.ImageArea}
                        />
                    </div>
                </div>
            </div>
            {/* 底部操作栏 */}
            <div className={styles.BottomBar}>
                <PicViewerBottom
                    image={image}
                    hasPrev={hasPrev}
                    hasNext={hasNext}
                    onPrev={onPrev}
                    onNext={onNext}
                    onStatusChange={onStatusChange}
                    onModalOpenChange={setIsModalOpen}
                />
            </div>
        </div>
    )
}

/**
 * MediaViewer - 媒体查看器
 * 根据 url 类型自动选择渲染 FabricCanvas（图片）或 VideoPlayer（视频）
 */
function MediaViewer({ url, className }) {
    if (isVideoUrl(url)) {
        return <VideoPlayer url={url} className={className} />
    }
    return <FabricCanvas url={url} className={className} />
}

/**
 * VideoPlayer - 视频播放器
 * 支持播放控制、自适应容器大小
 */
function VideoPlayer({ url, className }) {
    const videoRef = useRef(null)

    // url 变化时重置视频
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.load()
        }
    }, [url])

    return (
        <div className={`${className} ${styles.VideoArea}`}>
            {url && (
                <video
                    ref={videoRef}
                    className={styles.VideoPlayer}
                    controls
                    autoPlay
                    preload="metadata"
                    crossOrigin="anonymous"
                >
                    <source src={url} />
                </video>
            )}
        </div>
    )
}

/**
 * FabricCanvas - 独立的 Fabric.js 画布封装
 * 支持：滚轮缩放（以指针为中心）、鼠标拖拽平移、图片自动居中适配
 * 通过 url prop 控制加载的图片，内部自治生命周期
 */
function FabricCanvas({ url, className }) {
    const containerRef = useRef(null)
    const canvasRef = useRef(null)
    const fabricRef = useRef(null)
    const fabricImageRef = useRef(null)
    const loadIdRef = useRef(0) // 用于竞态控制，丢弃过期的图片加载结果

    // 初始化 fabric canvas，绑定缩放和拖拽事件
    useEffect(() => {
        if (!canvasRef.current) return
        const fc = new fabric.Canvas(canvasRef.current, {
            selection: false,
            renderOnAddRemove: true,
            backgroundColor: '#F5F6FA',
        })
        fabricRef.current = fc

        // 滚轮缩放（以鼠标指针为中心）
        fc.on('mouse:wheel', (opt) => {
            const e = opt.e
            e.preventDefault()
            e.stopPropagation()
            const delta = e.deltaY
            let zoom = fc.getZoom() * (ZOOM_STEP ** delta)
            zoom = Math.min(Math.max(MIN_ZOOM, zoom), MAX_ZOOM)
            fc.zoomToPoint({ x: e.offsetX, y: e.offsetY }, zoom)
            fc.requestRenderAll()
        })

        // 鼠标拖拽平移
        let isPanning = false
        let lastPosX = 0
        let lastPosY = 0

        fc.on('mouse:down', (opt) => {
            const e = opt.e
            if (e.button === 0) {
                isPanning = true
                lastPosX = e.clientX
                lastPosY = e.clientY
                fc.defaultCursor = 'grabbing'
                fc.requestRenderAll()
            }
        })

        fc.on('mouse:move', (opt) => {
            if (!isPanning) return
            const e = opt.e
            const dx = e.clientX - lastPosX
            const dy = e.clientY - lastPosY
            fc.relativePan(new fabric.Point(dx, dy))
            lastPosX = e.clientX
            lastPosY = e.clientY
        })

        fc.on('mouse:up', () => {
            isPanning = false
            fc.defaultCursor = 'grab'
            fc.requestRenderAll()
        })

        fc.defaultCursor = 'grab'
        fc.hoverCursor = 'grab'

        return () => {
            fc.dispose()
            fabricRef.current = null
        }
    }, [])

    // 监听容器尺寸变化，动态调整 canvas 大小并重居中
    useEffect(() => {
        const container = containerRef.current
        const fc = fabricRef.current
        if (!container || !fc) return

        const ro = new ResizeObserver((entries) => {
            const { width, height } = entries[0].contentRect
            if (width > 0 && height > 0) {
                fc.setDimensions({ width, height })
                if (fabricImageRef.current) {
                    fitImageToCanvas(fc, fabricImageRef.current)
                }
            }
        })
        ro.observe(container)
        return () => ro.disconnect()
    }, [])

    // url 变化时加载新图片，带竞态控制
    useEffect(() => {
        const fc = fabricRef.current
        if (!fc) return

        // 递增 loadId，使旧的加载回调失效
        const currentLoadId = ++loadIdRef.current

        // 清空画布
        fc.clear()
        fc.setBackgroundColor('#F5F6FA', fc.requestRenderAll.bind(fc))
        fabricImageRef.current = null

        if (!url) return

        fabric.Image.fromURL(url, (img) => {
            // 竞态检查：如果有更新的加载请求，丢弃当前结果
            if (currentLoadId !== loadIdRef.current) return
            if (!fabricRef.current) return
            img.set({
                selectable: false,
                evented: false,
                hasBorders: false,
                hasControls: false,
            })
            fc.add(img)
            fabricImageRef.current = img
            fitImageToCanvas(fc, img)
        }, { crossOrigin: 'anonymous' })
    }, [url])

    return (
        <div className={className} ref={containerRef}>
            <canvas ref={canvasRef} />
        </div>
    )
}

/** 将图片居中适配到画布（90% 填充） */
function fitImageToCanvas(fc, img) {
    const cw = fc.getWidth()
    const ch = fc.getHeight()
    const iw = img.width
    const ih = img.height
    if (!cw || !ch || !iw || !ih) return

    // 重置视口变换
    fc.setViewportTransform([1, 0, 0, 1, 0, 0])

    // 计算适配缩放比例
    const scale = Math.min(cw / iw, ch / ih, 1) * 0.9
    img.set({
        scaleX: scale,
        scaleY: scale,
        left: (cw - iw * scale) / 2,
        top: (ch - ih * scale) / 2,
    })
    img.setCoords()
    fc.requestRenderAll()
}
